import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import logger from './utils/logger';

// Styles
import './index.css';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { RBACProvider, useRBAC } from './contexts/RBACContext';
import { ActivityTrackingProvider, useActivityTracking } from './contexts/ActivityTrackingContext';
import { QueryProvider, clearAllCache } from './providers/QueryProvider';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import CallList from './pages/CallList';
import Campaigns from './pages/Campaigns';
import Inbox from './pages/Inbox';
import Settings from './pages/Settings';
import PublicBooking from './pages/PublicBooking';
import AdminPanel from './pages/AdminPanel';
import Signals from './pages/Signals';
import WebTracking from './pages/WebTracking';


// Components
import Layout from './components/layout/Layout';
import LeadDetail from './components/leads/LeadDetail';
import CalendarView from './components/calendar/CalendarView';
import BookingManagement from './components/booking/BookingManagement';
import EmailAutomationManager from './components/automation/EmailAutomationManager';
import EmailAutomationScheduler from './components/automation/EmailAutomationScheduler';
import EmailAutomationControl from './components/EmailAutomationControl';
import AdvancedEmailAutomationControl from './components/AdvancedEmailAutomationControl';
import AccessDeniedPopup from './components/auth/AccessDeniedPopup';
import ResetPassword from './components/auth/ResetPassword';

// Universal Lead System Components (with safe loading)
import LinkedinAutomationWrapper from './components/LinkedinAutomationWrapper';

// Valid page routes for URL-based navigation
const VALID_PAGES = [
  'dashboard', 'leads', 'calllist', 'campaigns', 'calendar', 'bookings',
  'inbox', 'automation', 'linkedin-automation',
  'settings', 'admin', 'signals', 'web-tracking'
];

// Main App Content
const AppContent = () => {
  const { currentUser, loading, isPasswordRecovery } = useAuth();
  const { allowedPages, canAccessPage, userRole } = useRBAC();
  const activityTracking = useActivityTracking();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get initial page from URL path (e.g., /campaigns -> campaigns)
  const getPageFromPath = () => {
    const path = location.pathname.replace('/', '');
    if (path && VALID_PAGES.includes(path)) {
      return path;
    }
    return null;
  };
  
  const [currentPage, setCurrentPage] = useState(null); // Start null, set after role is determined
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLanding, setShowLanding] = useState(true);
  const [campaignFilter, setCampaignFilter] = useState(null);
  
  // Track if initial role-based redirect has been done (persists across refresh via sessionStorage)
  const hasInitialRedirect = useRef(false);
  
  // Session storage key for tracking initial redirect
  const INITIAL_REDIRECT_KEY = 'crm_initial_redirect_done';

  // Set default page based on user role after login
  // ONE-TIME redirect: Only on fresh login, not on browser refresh
  // Moderator & User roles → Call List
  // Admin & Developer roles → Dashboard
  useEffect(() => {
    if (!userRole || !currentUser) return;
    
    // Check if initial redirect was already done (persisted in sessionStorage)
    const redirectDone = sessionStorage.getItem(INITIAL_REDIRECT_KEY);
    
    if (redirectDone && !hasInitialRedirect.current) {
      // Redirect was done before (browser refresh case)
      // Restore page from URL instead of forcing role-based redirect
      hasInitialRedirect.current = true;
      const urlPage = getPageFromPath();
      if (urlPage && canAccessPage(urlPage)) {
        setCurrentPage(urlPage);
      } else {
        // URL page not accessible, use role-based default
        if (userRole === 'moderator' || userRole === 'user') {
          setCurrentPage(allowedPages?.includes('calllist') ? 'calllist' : 'dashboard');
        } else {
          setCurrentPage('dashboard');
        }
      }
      return;
    }
    
    // First time login - do role-based redirect
    if (!hasInitialRedirect.current) {
      hasInitialRedirect.current = true;
      sessionStorage.setItem(INITIAL_REDIRECT_KEY, 'true');
      
      // Role-based default landing page
      // Moderator and User → Call List
      // Admin and Developer → Dashboard
      if (userRole === 'moderator' || userRole === 'user') {
        // Moderator/User default: calllist > dashboard > first allowed
        if (allowedPages && allowedPages.includes('calllist')) {
          setCurrentPage('calllist');
        } else if (allowedPages && allowedPages.includes('dashboard')) {
          setCurrentPage('dashboard');
        } else if (allowedPages && allowedPages.length > 0) {
          setCurrentPage(allowedPages[0]);
        } else {
          setCurrentPage('calllist'); // Fallback
        }
      } else {
        // Admin/Developer default: dashboard
        setCurrentPage('dashboard');
      }
    }
  }, [userRole, allowedPages, currentUser, canAccessPage]);
  
  // Reset initial redirect flag when user logs out and clean up URL
  useEffect(() => {
    if (!currentUser) {
      hasInitialRedirect.current = false;
      sessionStorage.removeItem(INITIAL_REDIRECT_KEY);
      setCurrentPage(null);
      // Clean up URL to prevent route leaking after logout
      const path = location.pathname;
      if (path !== '/' && path !== '/booking' && path !== '/reset-password') {
        navigate('/', { replace: true });
      }
    }
  }, [currentUser, navigate, location.pathname]);
  
  // Sync URL with current page (update browser URL when page changes)
  useEffect(() => {
    if (currentPage && currentUser) {
      const currentPath = location.pathname.replace('/', '');
      // Only update URL if it differs from current page
      if (currentPath !== currentPage) {
        navigate(`/${currentPage}`, { replace: true });
      }
    }
  }, [currentPage, currentUser, navigate, location.pathname]);

  const handlePageChange = (page) => {
    // Only allow navigation to pages user has access to
    if (!canAccessPage(page)) {
      logger.warn(`Access denied to page: ${page}`);
      return;
    }
    setCurrentPage(page);
    setSelectedLead(null); // Clear selected lead when changing pages
    if (page !== 'leads') {
      setCampaignFilter(null); // Clear campaign filter when not on leads page
    }
    // Track page visit
    if (activityTracking?.trackPageVisit) {
      activityTracking.trackPageVisit(page);
    }
  };

  // Track initial page load
  useEffect(() => {
    if (currentPage && activityTracking?.trackPageVisit) {
      activityTracking.trackPageVisit(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle campaign leads navigation
  useEffect(() => {
    const handleViewCampaignLeads = (event) => {
      setCampaignFilter(event.detail.campaignName);
      setCurrentPage('leads');
      setSelectedLead(null);
    };

    const handleViewLead = (event) => {
      setSelectedLead(event.detail.lead);
      setCurrentPage('leads');
    };

    window.addEventListener('viewCampaignLeads', handleViewCampaignLeads);
    window.addEventListener('viewLead', handleViewLead);
    return () => {
      window.removeEventListener('viewCampaignLeads', handleViewCampaignLeads);
      window.removeEventListener('viewLead', handleViewLead);
    };
  }, []);

  const handleViewLead = (lead) => {
    setSelectedLead(lead);
  };

  const handleBackToLeads = () => {
    setSelectedLead(null);
  };

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show loading while determining initial page (for authenticated users)
  if (currentUser && currentPage === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Setting up workspace...</p>
        </div>
      </div>
    );
  }

  // Check if this is the public booking page
  const isPublicBookingPage = window.location.pathname === '/booking';
  
  // Check if this is the password reset page
  const isResetPasswordPage = location.pathname === '/reset-password';

  // Show public booking page without authentication
  if (isPublicBookingPage) {
    return <PublicBooking />;
  }

  // Show reset password page (user arrives via email link)
  // Also show if PASSWORD_RECOVERY event was fired (prevents auto-redirect to dashboard)
  if (isResetPasswordPage || isPasswordRecovery) {
    return <ResetPassword />;
  }

  // Show landing page if user is not logged in and hasn't clicked get started
  if (!currentUser && showLanding) {
    return <LandingPage onGetStarted={() => setShowLanding(false)} />;
  }

  // Show auth page if user is not logged in but has clicked get started
  if (!currentUser) {
    return <AuthPage />;
  }

  // Show lead detail if a lead is selected
  if (selectedLead) {
    return (
      <Layout currentPage={currentPage} onPageChange={handlePageChange}>
        <LeadDetail lead={selectedLead} onBack={handleBackToLeads} />
      </Layout>
    );
  }

  // Render the appropriate page based on currentPage
  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard />;
      case 'leads':
        return <Leads onViewLead={handleViewLead} campaignFilter={campaignFilter} onClearCampaignFilter={() => setCampaignFilter(null)} onPageChange={handlePageChange} />;
      case 'calllist':
        return <CallList onViewLead={handleViewLead} />;
      case 'campaigns':
        return <Campaigns />;
      case 'calendar':
        return <CalendarView />;
      case 'bookings':
        return <BookingManagement />;
      case 'inbox':
        return <Inbox />;
      case 'automation':
        return <AdvancedEmailAutomationControl />;
      case 'linkedin-automation':
        return <LinkedinAutomationWrapper />;
      case 'settings':
        return <Settings />;
      case 'admin':
        return <AdminPanel />;
      case 'signals':
        return <Signals />;
      case 'web-tracking':
        return <WebTracking />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <Layout currentPage={currentPage} onPageChange={handlePageChange}>
      <EmailAutomationScheduler />
      <AnimatePresence mode="wait">
        {renderPage()}
      </AnimatePresence>
    </Layout>
  );
};

// RBAC Gate - blocks unregistered/unauthorized users and wraps DataProvider
const RBACGate = ({ children }) => {
  const { currentUser } = useAuth();
  const { accessDenied, accessDeniedReason, loading, userRole } = useRBAC();
  
  // Show loading while RBAC is checking
  // CRITICAL: Also block if user is logged in but role hasn't been determined yet
  if (loading || (currentUser && !userRole && !accessDenied)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }
  
  // Block access if denied - show popup and redirect to landing
  if (accessDenied) {
    const handleGoToLanding = async () => {
      try {
        const { supabase } = await import('./config/supabase');
        const { error: globalErr } = await supabase.auth.signOut({ scope: 'global' });
        if (globalErr) {
          await supabase.auth.signOut({ scope: 'local' });
        }
      } catch (error) {
        logger.error('Logout failed:', error);
      } finally {
        // Clear all Supabase tokens from localStorage
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('sb-') || key.includes('supabase')) {
            localStorage.removeItem(key);
          }
        });
        sessionStorage.removeItem('crm_initial_redirect_done');
        window.location.href = '/';
      }
    };
    
    // Determine reason type for popup
    const getReasonType = () => {
      if (accessDeniedReason?.includes('not registered')) return 'not_registered';
      if (accessDeniedReason?.includes('permission')) return 'no_permission';
      return 'unauthorized';
    };
    
    return (
      <AccessDeniedPopup 
        isOpen={true}
        reason={getReasonType()} 
        onGoToLanding={handleGoToLanding}
      />
    );
  }
  
  // CRITICAL: Don't render DataProvider if user is not properly authenticated
  // This prevents any data fetching for unauthorized users
  if (!currentUser) {
    return children; // Let AppContent handle showing landing/auth page
  }
  
  // Only provide data context AFTER RBAC validation passes
  // This prevents data leakage for unauthorized users
  return (
    <DataProvider>
      <ActivityTrackingProvider>
        {children}
      </ActivityTrackingProvider>
    </DataProvider>
  );
};

// Main App Component with Providers
// CRITICAL: DataProvider is now INSIDE RBACGate to prevent data fetching for unauthorized users
const App = () => {
  return (
    <QueryProvider>
      <Router>
        <AuthProvider>
          <RBACProvider>
            <RBACGate>
              <AppContent />
            </RBACGate>
          </RBACProvider>
        </AuthProvider>
      </Router>
    </QueryProvider>
  );
};

export default App;
